import type {
  Channel,
  NotificationCandidateSnapshot,
  NotificationPolicyDecision,
  NotificationPolicyEvaluationInput,
  NotificationRecipientDecision,
  NotificationRecoveryReason,
  NotificationSourceSnapshot,
} from '@statura/types';

const CHANNELS: readonly Channel[] = ['in_app', 'push', 'email', 'sms'];
const PURPOSE_ORDER = { PRIMARY: 0, SUPPORTING: 1, PARTICIPANT: 2, OBSERVER: 3 } as const;
const meaningful = (value: string) =>
  typeof value === 'string' && value.length > 0 && value === value.trim();
const instant = (value: string) =>
  typeof value === 'string' && /(?:Z|[+-]\d{2}:\d{2})$/.test(value) ? Date.parse(value) : NaN;

/** The identity excludes policy revisions and transport retries deliberately. */
export function notificationOccurrenceIdentity(source: NotificationSourceSnapshot): string {
  const identity = [
    source.tenantId,
    source.sourceKind,
    source.sourceId,
    source.episodeKey,
    source.occurrenceKey,
  ];
  if (!identity.every(meaningful)) throw new Error('Invalid notification source identity');
  return JSON.stringify(identity);
}

/** Shared deterministic preview/dispatch decision. Inputs must be server-resolved facts. */
export function evaluateNotificationPolicy(
  input: NotificationPolicyEvaluationInput,
): NotificationPolicyDecision {
  const { source, policy } = input;
  if (!Number.isSafeInteger(input.nowEpochMs))
    throw new Error('Invalid notification evaluation clock');
  const now = input.nowEpochMs;
  const due = source.responseDueAt === null ? Infinity : instant(source.responseDueAt);
  const condition = instant(source.conditionAt);
  const requiresResponse =
    !policy || policy.purpose === 'ACTION' || policy.purpose === 'PLATFORM_INCIDENT';
  const subtypeKnown = !!policy?.allowedSubtypes.includes(source.subtype);
  const classified = source.classifiedUrgency;
  const urgent =
    !policy ||
    !subtypeKnown ||
    classified === 'UNKNOWN' ||
    classified === 'URGENT' ||
    policy.urgencyClass === 'URGENT';
  const unclaimedMs = policy?.unclaimedMs ?? 60000;
  const validWindow =
    Number.isSafeInteger(unclaimedMs) &&
    unclaimedMs > 0 &&
    Number.isFinite(new Date(condition + unclaimedMs).getTime()) &&
    Number.isFinite(new Date(now + unclaimedMs).getTime());
  const safeWindow = validWindow ? (urgent ? Math.min(unclaimedMs, 60000) : unclaimedMs) : 60000;
  if (!Number.isFinite(new Date(now).getTime()))
    throw new Error('Invalid notification evaluation clock');
  const attentionDueAt = new Date(
    Math.min(
      Number.isFinite(condition) && Number.isFinite(new Date(condition + safeWindow).getTime())
        ? condition + safeWindow
        : now,
      Number.isFinite(due)
        ? due
        : Number.isFinite(new Date(now + safeWindow).getTime())
          ? now + safeWindow
          : now,
    ),
  ).toISOString();
  const result: NotificationPolicyDecision = {
    kind: 'COVERAGE_RECOVERY',
    occurrenceIdentity: null,
    policyVersion: policy?.version ?? null,
    urgent,
    dispatchMode: 'NONE',
    recipients: [],
    recovery: [],
    excluded: [],
    attentionDueAt,
  };
  const recover = (reason: NotificationRecoveryReason) => {
    if (!result.recovery.some((row) => row.reason === reason))
      result.recovery.push({
        reason,
        ownerRoute: 'PROVIDER_COVERAGE_WITH_PLATFORM_BACKSTOP',
        dueAt: attentionDueAt,
      });
  };
  try {
    result.occurrenceIdentity = notificationOccurrenceIdentity(source);
  } catch {
    recover('INVALID_SOURCE');
    return result;
  }
  if (!source.authorityVerified) {
    recover('SOURCE_AUTHORITY_UNVERIFIED');
    return result;
  }
  if (
    !meaningful(source.sourceRevision) ||
    !Number.isFinite(condition) ||
    condition > now ||
    (requiresResponse && !Number.isFinite(due)) ||
    Number.isNaN(due)
  ) {
    recover('INVALID_CLOCK');
    return result;
  }
  if (source.closed) {
    if (!meaningful(source.closureEvidenceId ?? '')) {
      recover('UNVERIFIED_SOURCE_CLOSURE');
      return result;
    }
    result.kind = 'SOURCE_CLOSED';
    return result;
  }
  if (!policy || policy.status === 'DRAFT' || policy.workflowType !== source.workflowType) {
    recover('POLICY_UNAVAILABLE');
    return result;
  }
  if (!validWindow || !meaningful(policy.key) || !meaningful(policy.version)) {
    recover('POLICY_UNAVAILABLE');
    return result;
  }
  if (policy.urgencyClass === 'TELEMETRY' && subtypeKnown && classified === 'ROUTINE') {
    result.kind = 'INFORMATION_ONLY';
    return result;
  }
  if (policy.urgencyClass === 'TELEMETRY') {
    recover('UNCLASSIFIED_EVENT');
    return result;
  }
  if (!subtypeKnown || classified === 'UNKNOWN') recover('UNCLASSIFIED_EVENT');
  if (!input.candidateSetComplete) {
    recover('RECIPIENT_SET_INCOMPLETE');
    return result;
  }
  if (!Number.isSafeInteger(input.requestedTier) || input.requestedTier < 0) {
    recover('INVALID_SOURCE');
    return result;
  }
  if (input.tenantCommunicationState !== 'ALLOWED') {
    recover('COMMUNICATION_BLOCKED');
    return result;
  }

  const eligible: NotificationCandidateSnapshot[] = [];
  for (const candidate of input.candidates) {
    let reason: string | null = null;
    if (
      candidate.tenantId !== source.tenantId ||
      !candidate.active ||
      !candidate.interactive ||
      !candidate.sourceAccess ||
      !candidate.scopeAccess ||
      !candidate.relationshipCurrent
    )
      reason = 'CURRENT_AUTHORITY_DENIED';
    else if (
      !Number.isSafeInteger(candidate.tier) ||
      candidate.tier < 0 ||
      !meaningful(candidate.assignmentKey) ||
      !meaningful(candidate.principalId)
    )
      reason = 'INVALID_ASSIGNMENT';
    else if (
      candidate.purpose === 'PRIMARY' &&
      !candidate.capabilities.includes(policy.responseCapability)
    )
      reason = 'RESPONSE_CAPABILITY_MISSING';
    else if (
      candidate.purpose === 'PRIMARY' &&
      policy.requiresQualification &&
      !candidate.qualificationCurrent
    )
      reason = 'QUALIFICATION_MISSING';
    else if (
      candidate.purpose === 'PRIMARY' &&
      policy.requiresDuty &&
      (!candidate.duty ||
        !meaningful(candidate.duty.assignmentId) ||
        !meaningful(candidate.duty.revision) ||
        !(instant(candidate.duty.validUntil) > now))
    )
      reason = 'DUTY_COVERAGE_MISSING';
    if (reason) result.excluded.push({ principalId: candidate.principalId, reason });
    else eligible.push(candidate);
  }
  const primary = eligible.filter((c) => c.purpose === 'PRIMARY' && c.tier >= input.requestedTier);
  const tier = primary.reduce((lowest, c) => Math.min(lowest, c.tier), Infinity);
  const responders = primary.filter((c) => c.tier === tier);
  if (requiresResponse && !responders.length) {
    recover('NO_ELIGIBLE_RESPONDER');
    return result;
  }
  const assignments = new Set(responders.map((c) => c.assignmentKey));
  const principals = new Set(responders.map((c) => c.principalId));
  if (
    requiresResponse &&
    (assignments.size !== 1 || (policy.primaryMode !== 'TEAM' && principals.size !== 1))
  ) {
    recover('AMBIGUOUS_RESPONSIBILITY');
    return result;
  }
  if (requiresResponse && tier > input.requestedTier) recover('PRIMARY_COVERAGE_GAP');

  const selected = eligible.filter(
    (c) =>
      !requiresResponse ||
      (c.purpose === 'PRIMARY'
        ? c.tier === tier
        : c.tier === tier || c.origin === 'PROTECTED' || c.origin === 'PARTICIPANT'),
  );
  if (!selected.length) {
    if (policy.purpose === 'INFORMATION') result.kind = 'INFORMATION_ONLY';
    else recover('NO_ELIGIBLE_RESPONDER');
    return result;
  }
  const byPrincipal = new Map<string, NotificationRecipientDecision>();
  for (const candidate of selected) {
    const existing = byPrincipal.get(candidate.principalId);
    const recipient: NotificationRecipientDecision = existing ?? {
      principalId: candidate.principalId,
      assignmentKey: candidate.assignmentKey,
      tier: candidate.tier,
      purpose: candidate.purpose,
      origins: [],
      channels: [],
      deferred: [],
    };
    if (PURPOSE_ORDER[candidate.purpose] < PURPOSE_ORDER[recipient.purpose]) {
      recipient.purpose = candidate.purpose;
      recipient.assignmentKey = candidate.assignmentKey;
      recipient.tier = candidate.tier;
    }
    if (!recipient.origins.includes(candidate.origin)) recipient.origins.push(candidate.origin);
    const requested = new Set(candidate.preferredChannels ?? policy.defaultChannels);
    const mandatory = new Set([
      ...(candidate.purpose === 'PRIMARY' ? policy.mandatoryChannels : []),
      ...(policy.mandatoryChannelsByPurpose?.[candidate.purpose] ?? []),
    ]);
    const parsedQuiet = candidate.quietUntil === null ? 0 : instant(candidate.quietUntil);
    const parsedBudget =
      candidate.budgetDeferredUntil === null ? 0 : instant(candidate.budgetDeferredUntil);
    // A broken discretionary preference must not withhold an otherwise valid
    // floor/bypass channel. Preserve delivery and surface the configuration gap.
    if (!Number.isFinite(parsedQuiet) || !Number.isFinite(parsedBudget)) recover('INVALID_CLOCK');
    const quiet = Number.isFinite(parsedQuiet) ? parsedQuiet : 0;
    const budget = Number.isFinite(parsedBudget) ? parsedBudget : 0;
    for (const channel of CHANNELS) {
      if (!requested.has(channel) && !mandatory.has(channel)) continue;
      if (
        !policy.allowedChannels.includes(channel) ||
        !input.availableChannels.includes(channel) ||
        !candidate.availableChannels.includes(channel) ||
        (channel !== 'in_app' && !input.externalEgressAllowed)
      ) {
        if (mandatory.has(channel)) recover('MANDATORY_CHANNEL_UNAVAILABLE');
        continue;
      }
      const quietDelay = policy.bypassQuietHoursChannels.includes(channel) ? 0 : quiet;
      const protectedAttention =
        mandatory.has(channel) &&
        (candidate.purpose === 'PRIMARY' || candidate.origin === 'PROTECTED');
      const budgetDelay =
        protectedAttention || policy.bypassBudgetChannels.includes(channel) ? 0 : budget;
      const delay = channel !== 'in_app' ? Math.max(quietDelay, budgetDelay) : 0;
      if (delay > now) {
        if (mandatory.has(channel) && delay > Date.parse(attentionDueAt))
          recover('PRIMARY_COVERAGE_GAP');
        if (!recipient.deferred.some((d) => d.channel === channel))
          recipient.deferred.push({
            channel,
            until: new Date(delay).toISOString(),
            reason: quietDelay >= budgetDelay ? 'QUIET_HOURS' : 'ATTENTION_BUDGET',
          });
      } else if (!recipient.channels.includes(channel)) recipient.channels.push(channel);
    }
    recipient.deferred = recipient.deferred.filter((d) => !recipient.channels.includes(d.channel));
    byPrincipal.set(candidate.principalId, recipient);
  }
  result.recipients = [...byPrincipal.values()].sort((a, b) =>
    a.principalId.localeCompare(b.principalId),
  );
  if (
    requiresResponse &&
    result.recipients.some(
      (recipient) =>
        recipient.purpose === 'PRIMARY' &&
        recipient.channels.length === 0 &&
        recipient.deferred.length > 0 &&
        recipient.deferred.every(
          (deferred) => Date.parse(deferred.until) > Date.parse(attentionDueAt),
        ),
    )
  )
    recover('PRIMARY_COVERAGE_GAP');
  if (
    result.recipients.some(
      (c) => c.purpose === 'PRIMARY' && c.channels.length === 0 && c.deferred.length === 0,
    )
  )
    recover('MANDATORY_CHANNEL_UNAVAILABLE');
  const hasChannels = result.recipients.some((r) => r.channels.length > 0);
  const hasDeferred = result.recipients.some((r) => r.deferred.length > 0);
  result.kind = hasChannels
    ? 'DELIVER'
    : hasDeferred
      ? 'DEFER'
      : !requiresResponse && policy.purpose === 'INFORMATION'
        ? 'INFORMATION_ONLY'
        : 'COVERAGE_RECOVERY';
  if (result.kind === 'COVERAGE_RECOVERY' && result.recovery.length === 0)
    recover('MANDATORY_CHANNEL_UNAVAILABLE');
  result.dispatchMode =
    result.kind === 'DELIVER'
      ? !subtypeKnown || policy.status === 'LEGACY_COMPATIBILITY'
        ? 'LEGACY_COMPATIBILITY'
        : 'CATALOGUE'
      : 'NONE';
  return result;
}
